import { useEffect, useState } from "react";

import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import {
  loadConversationQuestions,
  runBotpressConversation,
} from "../../data/api/botpressConversationRunner.js";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniTextField from "../ui/omniTextField";

// Dialog that runs a conversation against the bot programmatically (Chat API
// only, no visual webchat) and streams the transcript as it goes.
const BotpressProgrammaticRunDialog = props => {
  const {
    open,
    onClose,
    botODM,
    // Rerun mode: when set, the dialog loads this saved conversation's user
    // questions and replays them, rather than expecting messages to be typed.
    conversationId = null,
    // Start the run automatically once the questions have loaded (rerun mode).
    autoRun = false,
    // Called after a successful run with the new conversation's details. Lets the
    // caller re-point an existing assessment at the freshly-created conversation
    // (used by "Quick Rerun"). Awaited so the dialog stays busy until it resolves.
    onRunComplete = null,
    dialogTitle = "Run Conversation Programmatically",
    maxWidth = "sm",
    fullWidth = true,
  } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [messagesText, setMessagesText] = useState("");
  const [isPreparing, setIsPreparing] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [progressValue, setProgressValue] = useState(0);
  const [transcript, setTranscript] = useState([]);

  const handleProgress = event => {
    const total = event.total || 1;
    switch (event.phase) {
      case "creating-user":
        setStatusText("Creating user…");
        setProgressValue(2);
        break;
      case "creating-conversation":
        setStatusText("Starting conversation…");
        setProgressValue(5);
        break;
      case "sending":
        setStatusText(`Sending question ${event.index + 1} of ${total}…`);
        setProgressValue(Math.round((event.index / total) * 100));
        break;
      case "awaiting-reply":
        setStatusText(`Waiting for the bot to respond (${event.index + 1} of ${total})…`);
        break;
      case "reply":
        setProgressValue(Math.round(((event.index + 1) / total) * 100));
        break;
      case "done":
        setStatusText("Done.");
        setProgressValue(100);
        break;
      default:
        break;
    }
  };

  const runMessages = async messages => {
    if (messages.length === 0) {
      enqueueSnackbar("There are no questions to send.", { variant: "warning" });
      return;
    }

    setIsRunning(true);
    setTranscript([]);
    setProgressValue(0);
    setStatusText("Starting…");

    try {
      const startedAt = Date.now();
      const result = await runBotpressConversation({
        botODM,
        messages,
        onProgress: handleProgress,
      });
      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
      setTranscript(result.transcript);
      enqueueSnackbar(`Conversation completed (${result.conversationId}).`, { variant: "success" });

      // Let the caller record the run (e.g. re-point the assessment). Awaited so
      // the dialog stays busy — and Close stays disabled — until it finishes.
      if (typeof onRunComplete === "function") {
        setStatusText("Updating assessment…");
        await onRunComplete({
          conversationId: result.conversationId,
          elapsedSeconds,
          questionCount: messages.length,
        });
        setStatusText("Done.");
      }
    } catch (error) {
      console.error("Programmatic conversation run failed:", error);
      enqueueSnackbar(`Run failed: ${error.message}`, { variant: "error" });
      setStatusText("Run failed.");
    } finally {
      setIsRunning(false);
    }
  };

  const handleRun = () => {
    const messages = messagesText
      .split("\n")
      .map(line => line.trim())
      .filter(line => line.length > 0);

    runMessages(messages);
  };

  // Rerun mode: load the saved conversation's user questions, pre-fill the
  // editor, and (optionally) kick off the run automatically.
  useEffect(() => {
    if (!open || conversationId == null) {
      return undefined;
    }

    let cancelled = false;
    setIsPreparing(true);
    setStatusText("Loading questions from the conversation…");

    loadConversationQuestions({ botODM, conversationId })
      .then(questions => {
        if (cancelled) {
          return;
        }
        setMessagesText(questions.join("\n"));
        setIsPreparing(false);

        if (autoRun) {
          runMessages(questions);
        } else {
          setStatusText("");
        }
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        console.error("Failed to load conversation for rerun:", error);
        enqueueSnackbar("Failed to load the conversation's questions.", { variant: "error" });
        setIsPreparing(false);
        setStatusText("Failed to load questions.");
      });

    return () => {
      cancelled = true;
    };
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conversationId]);

  const onCloseHandler = () => {
    if (isRunning) {
      return;
    }
    setMessagesText("");
    setTranscript([]);
    setStatusText("");
    setProgressValue(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onCloseHandler} disableEscapeKeyDown maxWidth={maxWidth} fullWidth={fullWidth}>
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogMessage>
          This conversation's questions are replayed against the bot via the Chat API — no webchat
          window required. Progress is shown below.
        </DialogMessage>

        <OmniTextField
          label="Questions (one per line)"
          type="textInput"
          variant="outlined"
          id="programmaticMessages"
          rowCount={6}
          multiline
          value={messagesText}
          disabled={isRunning || isPreparing}
          onChange={event => setMessagesText(event.target.value)}
        />

        {(isRunning || isPreparing || statusText) && (
          <Box sx={{ marginTop: theme.spacing(1) }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
              {(isRunning || isPreparing) && <CircularProgress size={18} color="secondary" />}
              <Typography variant="body2" color="textSecondary">
                {statusText}
              </Typography>
            </Box>
            {(isRunning || isPreparing) && (
              <LinearProgress
                color="secondary"
                variant={isRunning ? "determinate" : "indeterminate"}
                value={progressValue}
                sx={{ marginTop: theme.spacing(1), borderRadius: theme.spacing(0.5) }}
              />
            )}
          </Box>
        )}

        {transcript.length > 0 && (
          <Box
            sx={{
              marginTop: theme.spacing(2),
              padding: theme.spacing(1),
              borderRadius: theme.spacing(1),
              border: `1px solid ${theme.palette.divider}`,
              maxHeight: "40vh",
              overflowY: "auto",
            }}
          >
            {transcript.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: entry.direction === "outgoing" ? "flex-end" : "flex-start",
                  marginBottom: theme.spacing(1),
                }}
              >
                <Box
                  sx={{
                    maxWidth: "80%",
                    padding: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    backgroundColor:
                      entry.direction === "outgoing"
                        ? theme.palette.secondary.main
                        : theme.palette.commonground.main,
                    color:
                      entry.direction === "outgoing"
                        ? theme.palette.secondary.contrastText
                        : theme.palette.commonground.contrastText,
                  }}
                >
                  <Typography variant="body2">{entry.text}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCloseHandler} color="secondary" variant="contained" disabled={isRunning}>
          Close
        </Button>
        <Button
          onClick={handleRun}
          color="secondary"
          variant="contained"
          disabled={isRunning || isPreparing || !messagesText.trim()}
        >
          {isRunning ? "Running…" : "Run"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BotpressProgrammaticRunDialog;
